"use client";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState, useEffect, useRef, useCallback } from "react";
import { MobileMenu } from "./header-sub-components/header-menu-logged-out";
import { MobileMenuLoggedIn } from "./header-sub-components/header-menu-logged-in";
import SearchModal from "./UI/search-modal";
import { LogOut, ChevronDown } from "lucide-react";
import { usePrivyWallet } from "@/hooks/usePrivyWallet";
import { WalletInfoDisplay } from "./UI/wallet-info-display";
import { useDeviceAndWalletDetection } from "@/hooks/useDeviceAndWalletDetection";
import { CustomPhantomModal } from "./CustomPhantomModal";
import { useLinkAccount, usePrivy } from "@privy-io/react-auth";
import { MYGEOTOKENS_WALLET_DISPLAY_NAME } from "@/constants/wallet-branding";

export const Header = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [isScrolled, setIsScrolled] = useState(false);
	const [isHydrated, setIsHydrated] = useState(false);
	const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
	const [isConnectingPhantom, setIsConnectingPhantom] = useState(false);

	// Custom Phantom Modal state
	const [isCustomPhantomModalOpen, setIsCustomPhantomModalOpen] =
		useState(false);
	const [customPhantomLoading, setCustomPhantomLoading] = useState(false);
	const [customPhantomError, setCustomPhantomError] = useState<string | null>(
		null
	);
	const {
		publicKey,
		isConnected,
		isSignedIn,
		login,
		connect,
		logout: disconnectWallet,
		user,
		authenticated,
	} = usePrivyWallet();
	const { isMobile, hasPhantomExtension } = useDeviceAndWalletDetection();

	// Direct Privy hook for controlled login
	const { login: privyLogin } = usePrivy();

	// Stable callbacks for useLinkAccount to prevent re-render cascades
	const onLinkSuccess = useCallback(({ user, linkedAccount }: { user: any; linkedAccount: any }) => {
		setIsCustomPhantomModalOpen(false);
		setCustomPhantomLoading(false);
		setCustomPhantomError(null);

		setTimeout(() => {
			window.location.reload();
		}, 1000);
	}, []);

	const onLinkError = useCallback((error: any) => {
		const raw: string = error?.message ?? error?.error ?? String(error ?? "");
		const isAlreadyLinked =
			raw.includes("linked_to_another_user") ||
			error?.code === "linked_to_another_user";
		const isCancelled = raw.toLowerCase().includes("cancel") || raw.toLowerCase().includes("closed");

		if (!isCancelled) {
			setCustomPhantomError(
				isAlreadyLinked
					? "This wallet is already registered to another account. Go to your Profile page and use Merge Accounts to combine them."
					: `Failed to link ${MYGEOTOKENS_WALLET_DISPLAY_NAME}. Please try again.`
			);
		}
		setCustomPhantomLoading(false);
	}, []);

	const { linkWallet } = useLinkAccount({
		onSuccess: onLinkSuccess,
		onError: onLinkError,
	});

	const pathName = usePathname();
	const [openSearch, setOpenSearch] = useState(false);
	const tabletAboutDropdownRef = useRef<HTMLDivElement>(null);
	const desktopAboutDropdownRef = useRef<HTMLDivElement>(null);

	// Use a ref so the useEffect below doesn't re-run when linkWallet identity changes
	const linkWalletRef = useRef(linkWallet);
	linkWalletRef.current = linkWallet;

	// Prevent hydration mismatch
	useEffect(() => {
		setIsHydrated(true);
	}, []);

	// Safety: Ensure body scroll is never stuck
	useEffect(() => {
		document.body.style.overflow = document.body.style.overflow || "";
		document.body.style.position =
			document.body.style.position === "fixed"
				? ""
				: document.body.style.position;

		return () => {
			if (document.body.style.position === "fixed") {
				const scrollY = document.body.style.top;
				document.body.style.overflow = "";
				document.body.style.position = "";
				document.body.style.top = "";
				document.body.style.width = "";
				document.body.style.paddingRight = "";
				if (scrollY) {
					window.scrollTo(0, parseInt(scrollY || "0") * -1);
				}
			}
		};
	}, []);

	// Handle wallet linking after successful authentication
	useEffect(() => {
		const handleWalletLinkingAfterAuth = async () => {
			const phantomAddressToLink = localStorage.getItem(
				"phantom_address_to_link"
			);

			if (authenticated && phantomAddressToLink) {
				try {
					localStorage.removeItem("phantom_address_to_link");

					const phantom = (window as any).solana;
					if (!phantom?.isPhantom) {
						throw new Error(
							`${MYGEOTOKENS_WALLET_DISPLAY_NAME} browser extension not found`
						);
					}

					await phantom.connect();

					await new Promise((resolve) => setTimeout(resolve, 1000));

					await linkWalletRef.current({
						walletList: ["phantom"] as const,
					});
				} catch (linkError: any) {
					setCustomPhantomError(
						`Failed to link ${MYGEOTOKENS_WALLET_DISPLAY_NAME}. Please try again.`
					);
					setCustomPhantomLoading(false);
				}
			}
		};

		if (isHydrated && authenticated) {
			handleWalletLinkingAfterAuth();
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authenticated, isHydrated]);

	useEffect(() => {
		let lastScrollY = window.scrollY;
		const handleScroll = () => {
			const currentScrollY = window.scrollY;
			// Use hysteresis: scroll down past 80px to shrink, scroll back up past 40px to expand.
			// This prevents the header's own size change from triggering an oscillating feedback loop.
			if (!isScrolled && currentScrollY > 80) {
				setIsScrolled(true);
			} else if (isScrolled && currentScrollY < 40) {
				setIsScrolled(false);
			}
			lastScrollY = currentScrollY;
		};
		window.addEventListener("scroll", handleScroll, { passive: true });
		return () => window.removeEventListener("scroll", handleScroll);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isScrolled]);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: Event) => {
			const targetNode = event.target as Node;
			const clickedInsideTablet =
				tabletAboutDropdownRef.current?.contains(targetNode) ?? false;
			const clickedInsideDesktop =
				desktopAboutDropdownRef.current?.contains(targetNode) ?? false;
			if (
				!clickedInsideTablet &&
				!clickedInsideDesktop
			) {
				setAboutDropdownOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		document.addEventListener("touchstart", handleClickOutside, { passive: true });
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			document.removeEventListener("touchstart", handleClickOutside);
		};
	}, []);

	const activeMenu = (url: string): boolean => {
		// Handle exact match and sub-routes
		if (url === "/drop-nft" && pathName.startsWith("/drop-nft")) {
			return true;
		}
		if (url === "/wallet" && pathName.startsWith("/wallet")) {
			return true;
		}
		return pathName === url;
	};

	const handleDirectPhantomConnection = async () => {
		if (!hasPhantomExtension) {
			console.error("Phantom extension not detected");
			return;
		}

		setIsConnectingPhantom(true);
		try {
			// Connect directly to Phantom extension
			const phantom = (window as any).solana;
			if (!phantom || !phantom.isPhantom) {
				throw new Error(
					`${MYGEOTOKENS_WALLET_DISPLAY_NAME} browser extension not found`
				);
			}

			// Request connection to Phantom
			const response = await phantom.connect();

			if (response.publicKey) {

				// Now use Privy's connect with wallet method to link this to Privy
				await connect("phantom");
			}
		} catch (error: any) {
			console.error(" Direct Phantom connection error:", error);
		} finally {
			setIsConnectingPhantom(false);
		}
	};

	// Custom Phantom Modal handlers
	const openCustomPhantomModal = () => {
		setCustomPhantomError(null);
		setIsCustomPhantomModalOpen(true);
	};

	const closeCustomPhantomModal = () => {
		setIsCustomPhantomModalOpen(false);
		setCustomPhantomLoading(false);
		setCustomPhantomError(null);
	};

	// Handle Custom Phantom Email Submit with wallet address
	const handleCustomPhantomEmailSubmit = async (
		email: string,
		walletAddress: string
	) => {
		setCustomPhantomLoading(true);
		setCustomPhantomError(null);

		try {
			// Call the phantom-connect API
			const response = await fetch("/api/user/phantom-connect", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					email,
					walletAddress,
				}),
			});

			const data = await response.json();

			if (!response.ok || !data.success) {
				throw new Error(data.message || "Connection failed");
			}

			// Handle different response actions
			if (data.data.action === "NEEDS_WALLET_LINKING") {
				// Existing user needs to link Phantom wallet - Two-step process
				try {
					// Store the phantom address for use after login
					localStorage.setItem("phantom_address_to_link", walletAddress);

					// First, authenticate the user with Privy (email only, no wallet options)
					await privyLogin({
						loginMethods: ["email"], // Only show email login
						disableSignup: true, // Only allow existing users to log in
					});

					// Step 2 happens in useEffect after authentication
				} catch (authError: any) {
					setCustomPhantomError("Failed to log in. Please try again.");
					setCustomPhantomLoading(false);
				}
				return;
			}

			// Legacy fallback for old NEEDS_LINKING action
			if (data.data.action === "NEEDS_LINKING") {
				setCustomPhantomError(
					`Account with this email already exists. Please use "Connect Wallet" to log in first, then you can link ${MYGEOTOKENS_WALLET_DISPLAY_NAME} on your wallet page.`
				);
				return;
			}

			// Success cases (CREATED, LOGIN, or WALLET_LINKED)
			if (data.data.token) {
				// Store JWT token (you might want to use a more secure method)
				localStorage.setItem("auth_token", data.data.token);

				// Close modal on success
				setIsCustomPhantomModalOpen(false);
				setCustomPhantomLoading(false);
				setCustomPhantomError(null);

				// Sync authentication state with Privy
				try {
					// Handle different authentication scenarios
					if (data.data.action === "LOGIN_WITH_PHANTOM_READY") {
						// Hybrid approach - user logged in with Phantom wallet ready
						// Store phantom wallet info in localStorage for the wallet switcher to use
						if (data.data.phantomWallet) {
							localStorage.setItem(
								"phantom_wallet_ready",
								data.data.phantomWallet
							);
						}
						await login();
					} else if (data.data.action === "WALLET_LINKED_LOGIN") {
						// Wallet successfully linked - trigger Privy login to see the new wallet
						await login();
					} else if (
						data.data.action === "LOGIN_WITH_NEW_WALLET" ||
						data.data.action === "LOGIN"
					) {
						// Use the standard Privy login for existing users
						await login();
					} else if (data.data.action === "CREATED") {
						// For new users, they're already authenticated via our custom flow
						// Just reload to reflect the new authentication state
						window.location.reload();
					}
				} catch (authSyncError) {
					// Fallback: Just reload the page
					window.location.reload();
				}
			}
		} catch (error: any) {
			// Handle specific error types
			if (error.message?.includes("WALLET_EMAIL_CONFLICT")) {
				setCustomPhantomError(
					"This wallet is already associated with a different email address."
				);
			} else if (error.message?.includes("WALLET_ALREADY_EXISTS")) {
				setCustomPhantomError(
					"This wallet is already registered with another account."
				);
			} else {
				setCustomPhantomError(
					error.message || "Connection failed. Please try again."
				);
			}
		} finally {
			setCustomPhantomLoading(false);
		}
	};

	const menuItems = [
		{
			label: "Profile",
			icon: "/icons/menu-icons/profile.svg",
			href: "/profile",
		},
		{
			label: "Drop Stash",
			icon: "/icons/menu-icons/drop.svg",
			href: "/drop-nft",
		},
		{
			label: "Wallet",
			icon: "/icons/menu-icons/wallet.svg",
			href: "/wallet",
		},
		{
			label: "Logout",
			icon: <LogOut className="text-black" />,
			onClick: async () => {
				try {
					await disconnectWallet();
				} catch (error) {
					console.error("Logout error:", error);
				}
			},
		},
	];

	return (
		<header
			className={`w-full z-50 sticky top-0 transition-all duration-300 will-change-transform ${
				isScrolled ? "py-2 lg:py-3" : "py-4 lg:py-6"
			} ${pathName === "/" ? "bg-black" : "bg-mgt-black/90 backdrop-blur-sm"}`}
		>
			<nav className="container mx-auto md:px-0 px-4">
				{/* Mobile layout (below md) - horizontal with mobile menu */}
				<div className="md:hidden flex justify-between items-center">
					{/* Left section */}
					<div className="flex items-center space-x-2">
						<button
							className="bg-white rounded-full p-2 hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-mgt-green"
							onClick={() => setOpenSearch(true)}
							aria-label="Open search"
						>
							<Image
								src="/icons/search.svg"
								width={20}
								height={20}
								alt="Search icon"
								className="min-w-[20px]"
							/>
						</button>
					</div>

				{/* Center logo */}
				<div
					className={`absolute left-1/2 transform -translate-x-1/2 ${
						isOpen ? "pointer-events-none" : ""
					}`}
				>
					<Link
						href="/"
						className="transform hover:scale-105 transition-transform duration-200"
					>
						<Image
							alt="MyGeoTokens - logo"
							src="/icons/logo.gif"
							width={105}
							height={105}
							className="cursor-pointer"
							priority
							unoptimized
						/>
					</Link>
				</div>

				{/* Right section - Mobile menu button */}
					<div className="flex items-center">
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								setIsOpen((prev) => !prev);
							}}
							aria-label={isOpen ? "Close menu" : "Open menu"}
							aria-expanded={isOpen}
							className="rounded-full bg-white p-2 hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-mgt-green touch-manipulation"
						>
							<Image
								width={24}
								height={24}
								src={isOpen ? "/icons/close.png" : "/icons/user.png"}
								alt={isOpen ? "Close menu" : "Open menu"}
								className={`min-w-[24px] ${
									isSignedIn && !isOpen
										? "[filter:invert(55%)_sepia(96%)_saturate(400%)_hue-rotate(93deg)_brightness(95%)_contrast(95%)]"
										: ""
								}`}
							/>
						</button>
					</div>
				</div>

				{/* Tablet layout (md to lg) - horizontal with all items visible */}
				<div className="hidden md:block lg:hidden">
					<div className="flex justify-between items-center">
						{/* Left section */}
						<div className="flex items-center space-x-4">
							<button
								className="bg-white rounded-full p-2 hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-mgt-green"
								onClick={() => setOpenSearch(true)}
								aria-label="Open search"
							>
								<Image
									src="/icons/search.svg"
									width={20}
									height={20}
									alt="Search icon"
									className="min-w-[20px]"
								/>
							</button>

							{/* About Dropdown for tablet */}
							<div ref={tabletAboutDropdownRef} className="relative">
								<button
									onClick={() => setAboutDropdownOpen(!aboutDropdownOpen)}
									className="flex items-center space-x-1 text-white hover:text-mgt-green transition-colors duration-200 text-xs font-medium"
								>
									<span>About</span>
									<ChevronDown
										className={`w-3 h-3 transition-transform duration-200 ${
											aboutDropdownOpen ? "rotate-180" : ""
										}`}
									/>
								</button>

								{aboutDropdownOpen && (
									<div className="absolute left-0 mt-2 w-48 bg-brown rounded-lg shadow-lg border border-brown/50 py-2 z-50">
										<Link
											href="/about"
											onClick={() => setAboutDropdownOpen(false)}
											className="block px-4 py-2 text-sm text-off-white hover:text-mgt-green hover:bg-brown/80 transition-colors"
										>
											About MyGeoTokens
										</Link>
										<Link
											href="/guide"
											onClick={() => setAboutDropdownOpen(false)}
											className="block px-4 py-2 text-sm text-off-white hover:text-mgt-green hover:bg-brown/80 transition-colors"
										>
											Start Guide
										</Link>
										<Link
											href="/faqs"
											onClick={() => setAboutDropdownOpen(false)}
											className="block px-4 py-2 text-sm text-off-white hover:text-mgt-green hover:bg-brown/80 transition-colors"
										>
											FAQs
										</Link>
										<Link
											href="/terms"
											onClick={() => setAboutDropdownOpen(false)}
											className="block px-4 py-2 text-sm text-off-white hover:text-mgt-green hover:bg-brown/80 transition-colors"
										>
											Terms & Conditions
										</Link>
										<Link
											href="/privacy"
											onClick={() => setAboutDropdownOpen(false)}
											className="block px-4 py-2 text-sm text-off-white hover:text-mgt-green hover:bg-brown/80 transition-colors"
										>
											Privacy Policy
										</Link>
									</div>
								)}
							</div>
						</div>

					{/* Center logo */}
					<div className="absolute left-1/2 transform -translate-x-1/2">
						<Link
							href="/"
							className="transform hover:scale-105 transition-transform duration-200"
						>
							<Image
								alt="MyGeoTokens - logo"
								src="/icons/logo.gif"
								width={105}
								height={105}
								className="cursor-pointer"
								unoptimized
							/>
						</Link>
					</div>

					{/* Right section */}
						<div className="flex items-center space-x-2">
							{/* Enhanced Wallet Status */}
							{isHydrated && isConnected && (
								<WalletInfoDisplay
									compact={true}
									showBalance={true}
									showNFTCounts={true}
									className="hidden sm:block"
								/>
							)}
							{isHydrated && !isConnected && (
								<div className="flex items-center space-x-1">
									<div className="w-2 h-2 rounded-full bg-gray-500"></div>
									<span className="text-xs text-gray-400">Not connected</span>
								</div>
							)}

							{isSignedIn ? (
								<ul className="flex items-center space-x-1">
									{menuItems.map((item) => (
										<li key={item.label}>
											{item.href ? (
												<Link
													href={item.href}
													className={`group flex items-center p-1 rounded-lg transition-colors duration-200 ${
														activeMenu(item.href)
															? "bg-mgt-black/10"
															: "hover:bg-white/10"
													}`}
													title={item.label}
													onClick={(e) => {
														// Ensure navigation works properly
													}}
												>
													<div
														className={`rounded-full p-1 bg-white ${
															activeMenu(item.href)
																? "ring-2 ring-mgt-green"
																: "group-hover:ring-2 group-hover:ring-mgt-green/50"
														}`}
													>
														{typeof item.icon === "string" ? (
															<Image
																src={item.icon}
																alt={`${item.label} icon`}
																height={16}
																width={16}
																className="min-w-[16px]"
															/>
														) : (
															<div className="w-4 h-4 flex items-center justify-center">
																{item.icon}
															</div>
														)}
													</div>
												</Link>
											) : (
												<button
													onClick={item.onClick}
													className="group flex items-center p-1 rounded-lg transition-colors duration-200 hover:bg-white/10"
													title={item.label}
												>
													<div className="rounded-full p-1 bg-white group-hover:ring-2 group-hover:ring-mgt-green/50">
														<div className="w-4 h-4 flex items-center justify-center">
															{item.icon}
														</div>
													</div>
												</button>
											)}
										</li>
									))}
								</ul>
							) : (
								<div className="flex items-center space-x-2">
									{!isMobile && hasPhantomExtension ? (
										// Show BOTH buttons for user choice
										<div className="flex items-center space-x-2">
											<button
												onClick={() => connect()}
												className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded-full transition-colors duration-200 text-xs font-medium"
											>
												Connect Wallet
											</button>
										</div>
									) : (
										<>
											<button
												onClick={() => connect()}
												className="text-white hover:text-mgt-green transition-colors duration-200 text-xs font-medium"
											>
												Login
											</button>
											<button
												onClick={() => connect()}
												className="bg-mgt-green text-white px-3 py-1 rounded-full hover:bg-mgt-green/90 transition-colors duration-200 text-xs font-medium"
											>
												Sign Up
											</button>
										</>
									)}
								</div>
							)}
						</div>
					</div>
				</div>

				{/* Desktop layout (lg and above) - vertical with logo on top */}
				<div className="hidden lg:block">
					{/* Top row: Logo */}
				<div className="flex justify-center mb-4">
					<Link
						href="/"
						className="transform hover:scale-105 transition-transform duration-200"
					>
						<Image
							alt="MyGeoTokens - logo"
							src="/icons/logo.gif"
							width={140}
							height={140}
							className="cursor-pointer"
							unoptimized
						/>
					</Link>
				</div>

				{/* Bottom row: Menu items */}
					<div className="flex justify-between items-center">
						{/* Left section */}
						<div className="flex items-center space-x-6">
							<button
								className="bg-white rounded-full p-2 hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-mgt-green"
								onClick={() => setOpenSearch(true)}
								aria-label="Open search"
							>
								<Image
									src="/icons/search.svg"
									width={24}
									height={24}
									alt="Search icon"
									className="min-w-[24px]"
								/>
							</button>

							{/* About Dropdown */}
							<div ref={desktopAboutDropdownRef} className="relative">
								<button
									onClick={() => setAboutDropdownOpen(!aboutDropdownOpen)}
									className="flex items-center space-x-1 text-white hover:text-mgt-green transition-colors duration-200 text-sm font-medium"
								>
									<span>About</span>
									<ChevronDown
										className={`w-4 h-4 transition-transform duration-200 ${
											aboutDropdownOpen ? "rotate-180" : ""
										}`}
									/>
								</button>

								{aboutDropdownOpen && (
									<div className="absolute left-0 mt-2 w-48 bg-brown rounded-lg shadow-lg border border-brown/50 py-2 z-50">
										<Link
											href="/about"
											onClick={() => setAboutDropdownOpen(false)}
											className="block px-4 py-2 text-sm text-off-white hover:text-mgt-green hover:bg-brown/80 transition-colors"
										>
											About MyGeoTokens
										</Link>
										<Link
											href="/guide"
											onClick={() => setAboutDropdownOpen(false)}
											className="block px-4 py-2 text-sm text-off-white hover:text-mgt-green hover:bg-brown/80 transition-colors"
										>
											Start Guide
										</Link>
										<Link
											href="/faqs"
											onClick={() => setAboutDropdownOpen(false)}
											className="block px-4 py-2 text-sm text-off-white hover:text-mgt-green hover:bg-brown/80 transition-colors"
										>
											FAQs
										</Link>
										<Link
											href="/terms"
											onClick={() => setAboutDropdownOpen(false)}
											className="block px-4 py-2 text-sm text-off-white hover:text-mgt-green hover:bg-brown/80 transition-colors"
										>
											Terms & Conditions
										</Link>
										<Link
											href="/privacy"
											onClick={() => setAboutDropdownOpen(false)}
											className="block px-4 py-2 text-sm text-off-white hover:text-mgt-green hover:bg-brown/80 transition-colors"
										>
											Privacy Policy
										</Link>
									</div>
								)}
							</div>
						</div>

						{/* Right section */}
						<div className="flex items-center space-x-6">
							{/* Enhanced Wallet Status */}
							{isHydrated && isConnected && (
								<WalletInfoDisplay
									compact={true}
									showBalance={true}
									showNFTCounts={true}
								/>
							)}
							{isHydrated && !isConnected && (
								<div className="flex items-center space-x-2">
									<div className="w-2 h-2 rounded-full bg-gray-500"></div>
									<span className="text-sm text-gray-400">
										Wallet not connected
									</span>
								</div>
							)}

							{isSignedIn ? (
								<ul className="flex items-center space-x-6">
									{menuItems.map((item) => (
										<li key={item.label}>
											{item.href ? (
												<Link
													href={item.href}
													className={`group flex items-center space-x-2 p-2 rounded-lg transition-colors duration-200 ${
														activeMenu(item.href)
															? "bg-mgt-black/10"
															: "hover:bg-white/10"
													}`}
													onClick={(e) => {
														// Ensure navigation works properly
													}}
												>
													<div
														className={`rounded-full p-1 bg-white ${
															activeMenu(item.href)
																? "ring-2 ring-mgt-green"
																: "group-hover:ring-2 group-hover:ring-mgt-green/50"
														}`}
													>
														{typeof item.icon === "string" ? (
															<Image
																src={item.icon}
																alt={`${item.label} icon`}
																height={24}
																width={24}
																className="min-w-[24px]"
															/>
														) : (
															<div className="w-6 h-6 flex items-center justify-center">
																{item.icon}
															</div>
														)}
													</div>
													<span className="text-white text-sm font-medium">
														{item.label}
													</span>
												</Link>
											) : (
												<button
													onClick={item.onClick}
													className="group flex items-center space-x-2 p-2 rounded-lg transition-colors duration-200 hover:bg-white/10"
												>
													<div className="rounded-full p-1 bg-white group-hover:ring-2 group-hover:ring-mgt-green/50">
														<div className="w-6 h-6 flex items-center justify-center">
															{item.icon}
														</div>
													</div>
													<span className="text-white text-sm font-medium">
														{item.label}
													</span>
												</button>
											)}
										</li>
									))}
								</ul>
							) : (
								<div className="flex items-center space-x-4">
									{hasPhantomExtension ? (
										// Show BOTH buttons for user choice
										<div className="flex items-center space-x-3">
											<button
												onClick={() => connect()}
												className="bg-mgt-green text-white px-4 py-2 rounded-full hover:bg-mgt-green/90 transition-colors duration-200 text-sm font-medium"
											>
												Login
											</button>
										</div>
									) : (
										<button
											onClick={() => connect()}
											className="bg-mgt-green text-white px-4 py-2 rounded-full hover:bg-mgt-green/90 transition-colors duration-200 text-sm font-medium"
										>
											Login
										</button>
									)}
								</div>
							)}
						</div>
					</div>
				</div>
			</nav>
			{(() => {
				return null;
			})()}
			{isOpen && isHydrated && isSignedIn && (
				<MobileMenuLoggedIn isOpen={isOpen} setIsOpen={setIsOpen} />
			)}
			{isOpen && isHydrated && !isSignedIn && (
				<MobileMenu isOpen={isOpen} setIsOpen={setIsOpen} />
			)}
			<SearchModal
				open={openSearch}
				onClose={() => setOpenSearch(false)}
				key="search-modal"
			/>

			{/* Custom Phantom Modal */}
			<CustomPhantomModal
				isOpen={isCustomPhantomModalOpen}
				onClose={closeCustomPhantomModal}
				onEmailSubmit={handleCustomPhantomEmailSubmit}
				isLoading={customPhantomLoading}
				error={customPhantomError}
			/>

			{/* Temporary debug panel - remove in production */}
			{/* Web3AuthDebugger removed - using Privy now */}
		</header>
	);
};
